/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import ExternalRoutes from 'services/external_routes';
import { RulePatchPayload, updateProposalRules } from 'services/actions/ai_management_proposal';
import {
  CLIENT_RULE_TEXT_LIMIT,
  isAdjustedRule,
  isClientRule,
  isControversialRule,
  isNumericRule,
  Leitura,
  Rule,
  RULE_STATUS_LABEL,
  RuleAction,
  RuleOrigin,
  RuleStatus,
  ruleAction,
  ruleChosenReading,
  ruleExtractedValue,
  ruleQuote,
  ruleReadings,
  ruleStatusOf,
  ruleTitle,
  ruleWhereToCheck,
  toAdjustedRulePatch,
  toClientRulePatch,
  toControversialRulePatch,
  toRevertedRulePatch,
  toRulePatch,
  proposalRuleTarget,
} from 'services/ai_management_proposal';
import { Container } from './styles';

interface AiManagementProposalRulesProps {
  biddingId: number;
  rules: Rule[];
  partial: boolean;
  modelMissing: boolean;
  onOpenEdital?: (rule?: Rule) => void;
  onContinue: () => void;
}

// Etapa em que a checagem confere cada tipo de regra (a mesma do "Corrigir" da revisão).
const CONFERIDA_EM: Record<string, string> = {
  validade_proposta: 'Empresa e condições',
  prazo_entrega: 'Empresa e condições',
  prazo_execucao: 'Empresa e condições',
  assinatura: 'Empresa e condições',
  marca_modelo_fabricante: 'Itens e preços',
  casas_decimais: 'Itens e preços',
  declaracoes: 'Declarações e documentos',
  documentos_complementares: 'Declarações e documentos',
  garantia_proposta: 'Declarações e documentos',
};

const STATUS_ICON: Record<RuleStatus, string> = {
  a_validar: 'pending',
  valida: 'check_circle_outline',
  nao_se_aplica: 'block',
};

const Icon: React.FC<{ name: string }> = ({ name }) => (
  <i className="material-icons-outlined" aria-hidden="true">
    {name}
  </i>
);

const formatDateTime = (value?: string | null) =>
  value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY [às] HH:mm') : null;

const pendingLabel = (count: number) =>
  count === 1
    ? 'Valide a regra pendente para continuar'
    : `Valide as ${count} regras pendentes para continuar`;

const AiManagementProposalRules: React.FC<AiManagementProposalRulesProps> = ({
  biddingId,
  rules,
  partial,
  modelMissing,
  onOpenEdital,
  onContinue,
}) => {
  const { trackSave, setPendencies, applyProposal, stepActions, footerActions } =
    useProposalShell();
  const [dismissingKey, setDismissingKey] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState('');
  const [adjustingKey, setAdjustingKey] = useState<string | null>(null);
  const [adjustValue, setAdjustValue] = useState('');
  const [addingRule, setAddingRule] = useState(false);
  const [newRule, setNewRule] = useState({ regra: '', ondeConferir: '', trecho: '' });
  const [undo, setUndo] = useState<{ rule: Rule } | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [choosing, setChoosing] = useState<{ key: string; acao: RuleAction } | null>(null);

  const pendingCount = useMemo(
    () => rules.filter((rule) => ruleStatusOf(rule) === 'a_validar').length,
    [rules],
  );

  // O nome do tipo é único na taxonomia; o índice desempata as regras do cliente (texto livre).
  const keyOf = (rule: Rule, index: number) => `${rule.regra}#${index}`;

  // O back sempre recebe a lista inteira de regras.
  const persist = (payload: RulePatchPayload[]) => {
    setSaveError('');
    return trackSave(updateProposalRules(biddingId, payload))
      .then(({ data }) => applyProposal(data))
      .catch(() => setSaveError('Não foi possível gravar as regras. Tente de novo.'));
  };

  const baseOf = (rule: Rule): RulePatchPayload =>
    toRulePatch(
      rule,
      ruleStatusOf(rule),
      typeof rule.motivo_nao_se_aplica === 'string' ? rule.motivo_nao_se_aplica : null,
    );

  const saveRules = (
    nextRules: Rule[],
    changeStatus: (rule: Rule, index: number) => RuleStatus,
    reasonOf: (rule: Rule, index: number) => string | null,
  ) =>
    persist(
      nextRules.map((rule, index) =>
        toRulePatch(rule, changeStatus(rule, index), reasonOf(rule, index)),
      ),
    );

  const setOne = (targetIndex: number, status: RuleStatus, motivo?: string | null) =>
    saveRules(
      rules,
      (rule, index) => (index === targetIndex ? status : ruleStatusOf(rule)),
      (rule, index) =>
        index === targetIndex
          ? motivo ?? null
          : typeof rule.motivo_nao_se_aplica === 'string'
          ? rule.motivo_nao_se_aplica
          : null,
    );

  const confirmAllPending = () =>
    saveRules(
      rules,
      (rule) => {
        if (ruleStatusOf(rule) !== 'a_validar') return ruleStatusOf(rule);
        if (isControversialRule(rule) && ruleChosenReading(rule) === null) return 'a_validar';
        return 'valida';
      },
      (rule) => (typeof rule.motivo_nao_se_aplica === 'string' ? rule.motivo_nao_se_aplica : null),
    );

  const saveAdjust = (targetIndex: number, novoValor: string) => {
    const target = rules[targetIndex];
    const extraido =
      ruleExtractedValue(target) ?? (typeof target.valor === 'string' ? target.valor : '');
    const payload = rules.map((rule, index) =>
      index === targetIndex ? toAdjustedRulePatch(rule, novoValor, extraido) : baseOf(rule),
    );
    persist(payload);
    setAdjustingKey(null);
    setAdjustValue('');
  };

  const revertAdjust = (targetIndex: number) => {
    const target = rules[targetIndex];
    const extraido = ruleExtractedValue(target);
    if (extraido === null) return;
    const origemOriginal: RuleOrigin = target.origem === 'resumo' ? 'resumo' : 'edital';
    const payload = rules.map((rule, index) =>
      index === targetIndex ? toRevertedRulePatch(rule, origemOriginal, extraido) : baseOf(rule),
    );
    persist(payload);
  };

  const saveNewRule = () => {
    persist([
      ...rules.map(baseOf),
      toClientRulePatch(newRule.regra, newRule.ondeConferir, newRule.trecho),
    ]);
    setAddingRule(false);
    setNewRule({ regra: '', ondeConferir: '', trecho: '' });
  };

  const deleteRule = (targetIndex: number) => {
    const removed = rules[targetIndex];
    persist(rules.filter((_, index) => index !== targetIndex).map(baseOf));
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo({ rule: removed });
    undoTimer.current = setTimeout(() => setUndo(null), 6000);
  };

  const undoDelete = () => {
    if (!undo) return;
    if (undoTimer.current) clearTimeout(undoTimer.current);
    persist([...rules.map(baseOf), baseOf(undo.rule)]);
    setUndo(null);
  };

  const openDrLicita = () =>
    window.open(ExternalRoutes.dr_licita_create_document, '_blank', 'noopener,noreferrer');

  const requestAction = (targetIndex: number, acao: RuleAction) => {
    const payload = rules.map((rule, index) =>
      index === targetIndex ? toControversialRulePatch(rule, acao) : baseOf(rule),
    );
    persist(payload);
    openDrLicita();
  };

  const chooseReading = (targetIndex: number, acao: RuleAction, leituraIndex: number) => {
    const payload = rules.map((rule, index) =>
      index === targetIndex ? toControversialRulePatch(rule, acao, leituraIndex) : baseOf(rule),
    );
    persist(payload);
    setChoosing(null);
  };

  // Desfazer numa controversa limpa a ação e a leitura: a regra volta às três ações.
  const undoControversial = (targetIndex: number) => {
    const payload = rules.map((rule, index) =>
      index === targetIndex
        ? { ...toRulePatch(rule, 'a_validar'), acao: null, leitura_escolhida: null }
        : baseOf(rule),
    );
    persist(payload);
  };

  const openDismiss = (key: string) => {
    setDismissingKey(key);
    setReason('');
  };

  const confirmDismiss = (index: number) => {
    setOne(index, 'nao_se_aplica', reason);
    setDismissingKey(null);
    setReason('');
  };

  useEffect(() => {
    setPendencies('regras', pendingCount);
  }, [pendingCount, setPendencies]);

  useEffect(
    () => () => {
      if (undoTimer.current) clearTimeout(undoTimer.current);
    },
    [],
  );

  const renderRuleMeta = (rule: Rule) => {
    const status = ruleStatusOf(rule);
    if (status === 'nao_se_aplica') {
      const who = typeof rule.confirmada_por_nome === 'string' ? rule.confirmada_por_nome : null;
      const when = formatDateTime(rule.confirmada_em);
      const parts = ['Não se aplica'];
      if (who) parts.push(`por ${who}`);
      if (when) parts.push(`em ${when}`);
      return parts.join(' · ');
    }
    if (isClientRule(rule)) return 'Adicionada por você';
    if (isControversialRule(rule) && ruleChosenReading(rule) !== null) {
      const acao = ruleAction(rule);
      if (acao === 'assumir_risco') return 'Você assumiu o risco';
      if (acao === 'resolvida_no_planejamento') return 'Dúvida resolvida no Planejamento';
      return 'Leitura escolhida';
    }
    if (isAdjustedRule(rule)) {
      const extraido = ruleExtractedValue(rule);
      return extraido ? `Ajustada por você · era ${extraido}` : 'Ajustada por você';
    }
    const etapa = CONFERIDA_EM[rule.regra];
    return `Extraída do edital · Conferida ${etapa ? `em ${etapa}` : 'na revisão'}`;
  };

  const renderAbrirEdital = (rule: Rule) =>
    onOpenEdital && (
      <button
        type="button"
        className="btn btn-link p-0 open-edital"
        onClick={() => onOpenEdital(rule)}
      >
        Abrir edital
        <Icon name="open_in_new" />
      </button>
    );

  const renderReadingOptions = (rule: Rule, index: number, acao: RuleAction) => (
    <div className="proposal-rule-readings mt-2">
      {ruleReadings(rule).map((leitura: Leitura, leituraIndex: number) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={leituraIndex} className="proposal-reading border rounded p-2 mb-2">
          {leitura.valor && <p className="mb-1">{leitura.valor}</p>}
          {leitura.trecho && (
            <blockquote className="proposal-rule-quote mb-1">
              &ldquo;{leitura.trecho}&rdquo;
            </blockquote>
          )}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => chooseReading(index, acao, leituraIndex)}
          >
            Usar esta leitura
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setChoosing(null)}>
        Cancelar
      </button>
    </div>
  );

  const renderControversial = (rule: Rule, index: number, key: string) => {
    const acao = ruleAction(rule);
    const isChoosing = choosing?.key === key;
    const requested = acao === 'impugnar' || acao === 'esclarecer';

    return (
      <li
        key={key}
        className="rule-card controversial"
        data-proposal-target={proposalRuleTarget(rule.regra)}
      >
        <div className="rule-card-head">
          <span className="rule-status-badge status-controversa">
            <Icon name="error_outline" />
            Regra controversa
          </span>
          <span className="rule-meta">{renderRuleMeta(rule)}</span>
        </div>
        <h3>{ruleTitle(rule)}</h3>
        {rule.motivo && <p className="rule-detail">{rule.motivo}</p>}

        {ruleReadings(rule).map((leitura: Leitura, leituraIndex: number) => (
          // eslint-disable-next-line react/no-array-index-key
          <blockquote key={leituraIndex}>
            {leitura.trecho ? <>&ldquo;{leitura.trecho}&rdquo;</> : leitura.valor}
            <small>
              {leitura.trecho && leitura.valor && <>{leitura.valor} · </>}
              {renderAbrirEdital(rule)}
            </small>
          </blockquote>
        ))}

        {requested && (
          <p className="text-muted small mb-2">
            {acao === 'impugnar' ? 'Impugnação solicitada' : 'Esclarecimento solicitado'}
          </p>
        )}

        {isChoosing ? (
          renderReadingOptions(rule, index, choosing.acao)
        ) : (
          <div className="rule-actions">
            {!requested && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => requestAction(index, 'impugnar')}
                >
                  <Icon name="gavel" />
                  Impugnar o edital
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => requestAction(index, 'esclarecer')}
                >
                  <Icon name="help_outline" />
                  Solicitar esclarecimento
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setChoosing({ key, acao: 'assumir_risco' })}
                >
                  <Icon name="error_outline" />
                  Assumir o risco
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => setChoosing({ key, acao: 'resolvida_no_planejamento' })}
                >
                  Já resolvi essa dúvida no Planejamento
                </button>
              </>
            )}
            {requested && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setChoosing({ key, acao: 'assumir_risco' })}
              >
                Escolher a leitura
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderRule = (rule: Rule, index: number) => {
    const key = keyOf(rule, index);
    const status = ruleStatusOf(rule);
    const quote = ruleQuote(rule);
    const where = ruleWhereToCheck(rule);
    const motivo = typeof rule.motivo_nao_se_aplica === 'string' ? rule.motivo_nao_se_aplica : '';
    const isDismissing = dismissingKey === key;
    const isAdjusting = adjustingKey === key;
    const controversialPending = isControversialRule(rule) && ruleChosenReading(rule) === null;

    if (controversialPending) {
      return renderControversial(rule, index, key);
    }

    return (
      <li
        key={key}
        className={`rule-card status-${status}`}
        data-proposal-target={proposalRuleTarget(rule.regra)}
      >
        <div className="rule-card-head">
          <span className={`rule-status-badge status-badge-${status}`}>
            <Icon name={STATUS_ICON[status]} />
            {RULE_STATUS_LABEL[status]}
          </span>
          <span className="rule-meta">{renderRuleMeta(rule)}</span>
        </div>
        <h3>{ruleTitle(rule)}</h3>
        {where && <p className="rule-detail">Onde conferir: {where}</p>}
        {status === 'nao_se_aplica' && motivo && <p className="rule-detail">Motivo: {motivo}</p>}
        {quote ? (
          <blockquote>
            &ldquo;{quote}&rdquo;
            <small>{renderAbrirEdital(rule)}</small>
          </blockquote>
        ) : (
          onOpenEdital && <p className="rule-edital">{renderAbrirEdital(rule)}</p>
        )}

        {isDismissing && (
          <div className="proposal-rule-dismiss mt-2">
            <label className="small mb-1 d-block" htmlFor={`motivo-${key}`}>
              Motivo (opcional)
              <textarea
                id={`motivo-${key}`}
                className="form-control form-control-sm mt-1 mb-2"
                rows={2}
                value={reason}
                maxLength={500}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <div className="d-flex">
              <button
                type="button"
                className="btn btn-sm btn-primary mr-2"
                onClick={() => confirmDismiss(index)}
              >
                Marcar como não se aplica
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => setDismissingKey(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {isAdjusting && (
          <div className="proposal-rule-adjust mt-2">
            <label className="small mb-1 d-block" htmlFor={`ajuste-${key}`}>
              O Analista leu {ruleExtractedValue(rule) ?? rule.valor}
              <input
                id={`ajuste-${key}`}
                type="text"
                className="form-control form-control-sm mt-1 mb-2"
                value={adjustValue}
                maxLength={CLIENT_RULE_TEXT_LIMIT}
                onChange={(event) => setAdjustValue(event.target.value)}
              />
            </label>
            <div className="d-flex">
              <button
                type="button"
                className="btn btn-sm btn-primary mr-2"
                onClick={() => saveAdjust(index, adjustValue)}
                disabled={!adjustValue.trim()}
              >
                Salvar ajuste
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => setAdjustingKey(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {!isDismissing && !isAdjusting && (
          <div className="rule-actions">
            {status === 'a_validar' && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setOne(index, 'valida')}
              >
                <Icon name="check" />
                Confirmar regra
              </button>
            )}
            {status !== 'a_validar' && (
              <button
                type="button"
                className="btn btn-sm btn-link undo-validation"
                onClick={() =>
                  isControversialRule(rule) ? undoControversial(index) : setOne(index, 'a_validar')
                }
              >
                Desfazer validação
              </button>
            )}
            {isNumericRule(rule) && !isAdjustedRule(rule) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setAdjustingKey(key);
                  setAdjustValue(typeof rule.valor === 'string' ? rule.valor : '');
                }}
              >
                <Icon name="edit" />
                Ajustar
              </button>
            )}
            {isAdjustedRule(rule) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => revertAdjust(index)}
              >
                Voltar ao valor extraído
              </button>
            )}
            {status === 'a_validar' && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => openDismiss(key)}
              >
                Não se aplica
              </button>
            )}
            {isClientRule(rule) && (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger"
                onClick={() => deleteRule(index)}
              >
                Excluir
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  const newRuleValid = newRule.regra.trim().length > 0 && newRule.ondeConferir.trim().length > 0;

  const renderAddRule = () =>
    addingRule ? (
      <div className="rule-card rule-add">
        <label className="small mb-1 d-block" htmlFor="nova-regra-texto">
          Regra
          <input
            id="nova-regra-texto"
            type="text"
            className="form-control form-control-sm mt-1 mb-2"
            value={newRule.regra}
            maxLength={CLIENT_RULE_TEXT_LIMIT}
            onChange={(event) => {
              const { value } = event.target;
              setNewRule((prev) => ({ ...prev, regra: value }));
            }}
          />
        </label>
        <label className="small mb-1 d-block" htmlFor="nova-regra-onde">
          Onde conferir
          <input
            id="nova-regra-onde"
            type="text"
            className="form-control form-control-sm mt-1 mb-2"
            value={newRule.ondeConferir}
            onChange={(event) => {
              const { value } = event.target;
              setNewRule((prev) => ({ ...prev, ondeConferir: value }));
            }}
          />
        </label>
        <label className="small mb-1 d-block" htmlFor="nova-regra-trecho">
          Trecho do edital (opcional)
          <textarea
            id="nova-regra-trecho"
            className="form-control form-control-sm mt-1 mb-2"
            rows={2}
            value={newRule.trecho}
            onChange={(event) => {
              const { value } = event.target;
              setNewRule((prev) => ({ ...prev, trecho: value }));
            }}
          />
        </label>
        <div className="d-flex">
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            onClick={saveNewRule}
            disabled={!newRuleValid}
          >
            Adicionar regra
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => {
              setAddingRule(false);
              setNewRule({ regra: '', ondeConferir: '', trecho: '' });
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    ) : null;

  const validas = rules.filter((rule) => ruleStatusOf(rule) === 'valida').length;
  const naoSeAplicam = rules.filter((rule) => ruleStatusOf(rule) === 'nao_se_aplica').length;
  const resolvidas = rules.length - pendingCount;
  const percentual = rules.length > 0 ? Math.round((resolvidas / rules.length) * 100) : 0;

  return (
    <Container className="proposal-rules">
      {partial && (
        <div className="alert alert-warning py-2">
          A análise não ficou completa. Confira no edital antes de seguir.
        </div>
      )}
      {modelMissing && (
        <div className="alert alert-info py-2">
          O edital não traz um modelo de proposta. A proposta usará o layout simples montado com as
          exigências do edital.
        </div>
      )}

      {stepActions &&
        createPortal(
          <button
            type="button"
            className="btn btn-outline-secondary step-add-rule"
            onClick={() => setAddingRule(true)}
            disabled={addingRule}
          >
            <Icon name="add" />
            Adicionar regra
          </button>,
          stepActions,
        )}

      {renderAddRule()}

      {saveError && <div className="alert alert-danger">{saveError}</div>}

      {rules.length === 0 ? (
        <p className="text-muted">Nenhuma regra para checar ainda.</p>
      ) : (
        <>
          <section className="progress-card">
            <div className="progress-head">
              <strong>
                {resolvidas} de {rules.length}{' '}
                {rules.length === 1 ? 'regra validada' : 'regras validadas'}
              </strong>
              <span>{percentual}%</span>
            </div>
            <div className="progress-bar-track">
              <span style={{ width: `${percentual}%` }} />
            </div>
            <div className="progress-summary">
              <span className="text-success">
                <Icon name="check_circle_outline" />
                {validas} {validas === 1 ? 'válida' : 'válidas'}
              </span>
              <span className="text-muted">
                <Icon name="block" />
                {naoSeAplicam} {naoSeAplicam === 1 ? 'não se aplica' : 'não se aplicam'}
              </span>
              <span className="text-attention">
                <Icon name="pending" />
                {pendingCount} a validar
              </span>
              {pendingCount > 0 && (
                <button
                  type="button"
                  className="btn btn-link p-0 confirm-all"
                  onClick={confirmAllPending}
                >
                  Confirmar todas as pendentes
                </button>
              )}
            </div>
          </section>
          <ul className="rules-list">{rules.map(renderRule)}</ul>
        </>
      )}

      {undo && (
        <div className="proposal-rule-undo alert alert-dark d-flex align-items-center justify-content-between py-2 mt-3">
          <span>Regra excluída.</span>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={undoDelete}>
            Desfazer
          </button>
        </div>
      )}

      {footerActions &&
        createPortal(
          <>
            {pendingCount > 0 && (
              <small className="footer-hint">{pendingLabel(pendingCount)}.</small>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={onContinue}
              disabled={pendingCount > 0}
            >
              Confirmar regras e continuar
            </button>
          </>,
          footerActions,
        )}
    </Container>
  );
};

export default AiManagementProposalRules;
